const BASE_URL = import.meta.env.VITE_API_URL || "";

export class ApiError extends Error {
  status: number;
  payload: unknown;
  retryAfter?: number;
  constructor(message: string, status: number, payload: unknown, retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
    this.retryAfter = retryAfter;
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Fastify rejects requests that advertise Content-Type: application/json but
  // send no body. Only set the header when we actually have a body — otherwise
  // DELETEs (and other body-less calls) fail 400 with "Body cannot be empty".
  const hasBody = init?.body !== undefined && init.body !== null;
  const headers: Record<string, string> = {
    ...(hasBody ? { "content-type": "application/json" } : {}),
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    ...init,
    headers,
  });
  if (!res.ok) {
    const text = await res.text();
    // Prefer the backend's own error message when available
    // ({ error: "...", message: "..." }). Fall back to the raw body.
    let payload: unknown = text;
    let message = text || `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string; message?: string };
      payload = parsed;
      message = parsed.error ?? parsed.message ?? message;
    } catch {
      /* not JSON — keep the raw text */
    }
    let retryAfter: number | undefined;
    if (res.status === 429) {
      const raw = res.headers.get("retry-after");
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed) && parsed > 0) retryAfter = Math.ceil(parsed);
    }
    throw new ApiError(message, res.status, payload, retryAfter);
  }
  return (await res.json()) as T;
}
